import React, { useState, useMemo, useEffect } from "react";
import { TariffListItem } from "../../types";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn, TableAction } from "@shared/types/table";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { PAGE_SIZE } from "@shared/utils/constants";
import { logEvent } from "@shared/analytics/analytics";
import { tarrifFilters } from "../../types";
import { FilterPanel } from "../Filters";
import { EditRateModal } from "../EditRateModal";
import { DeleteConfirmationDialog } from "@shared/components/DeleteConfirmationDialog";
import { useCloneRate, useTarrifList, useToggleRateMutation } from "../../hooks";

const TarrifList: React.FC = () => {
  const navigate = useNavigate();
  const toggleRateMutation = useToggleRateMutation();
  const [searchParams, setSearchParams] = useSearchParams();
  const remoteUtilityId = useRemoteUtilityId();
  const itemsPerPage = PAGE_SIZE;

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedRate, setSelectedRate] = useState<TariffListItem | null>(null);
  const [isToggleModalOpen, setIsToggleModalOpen] = useState(false);
  const [rowToToggle, setRowToToggle] = useState<TariffListItem | null>(null);

  // Parse filters from URL params
  const getFiltersFromUrl = (): tarrifFilters => {
    const page = parseInt(searchParams.get("page") || "1");

    const urlFilters: tarrifFilters = {
      page,
      limit: itemsPerPage,
      remote_utility_id: Number(remoteUtilityId),
    };

      // Parse createdBy
    const createdByParam = searchParams.get("createdBy");
    if (createdByParam) {
      urlFilters.createdBy = createdByParam;
    }

    // Parse utilityService
    const utilityServiceParam = searchParams.get("utilityService");
    if (utilityServiceParam) {
      urlFilters.utilityService = utilityServiceParam;
    }

    // Parse rateType
    const rateTypeParam = searchParams.get("rateType");
    if (rateTypeParam) {
      urlFilters.rateType = rateTypeParam;
    }

    // Parse search query
    const queryParam = searchParams.get("query");
    if (queryParam) {
      urlFilters.search_data = queryParam;
    }

    return urlFilters;
  };

  const [filters, setFilters] = useState<tarrifFilters>(getFiltersFromUrl);

  // Update filters when URL changes
  useEffect(() => {
    const newFilters = getFiltersFromUrl();
    setFilters(newFilters);
  }, [searchParams, remoteUtilityId]);

  // Call API with filters - THIS IS THE KEY FIX!
  const {
    data,
    isLoading: loading,
    error,
  } = useTarrifList(filters, remoteUtilityId);

  const {mutateAsync : cloneRate} = useCloneRate();


  const handleApplyFilters = (newFilters: Partial<tarrifFilters>) => {
    const params = new URLSearchParams(searchParams);

    // Clear existing filter params
    params.delete("status");
    params.delete("utilityService");
    params.delete("createdBy");

    params.delete("rateType");
    params.delete("page");

    // ✅ Apply the filters with correct parameter names
    if (newFilters.utilityService) {
      params.set("utilityService", newFilters.utilityService);
    }
    if (newFilters.rateType) {
      params.set("rateType", newFilters.rateType);
    }
      if (newFilters.createdBy) {
      params.set("createdBy", newFilters.createdBy);
    }

    // Reset to page 1 when applying filters
    params.set("page", "1");
    setSearchParams(params);
  };

  const handleResetFilters = () => {
    const params = new URLSearchParams(searchParams);

    // Remove all filter params
    params.delete("status");
    params.delete("utilityService");
    params.delete("createdBy");
    params.delete("rateType");
    params.delete("page");
    params.delete("query");

    setSearchParams(params);
  };

  // Handle user status toggle
  const handleStatusToggle = (rate: TariffListItem) => {
    const payload = {
      id: rate.id,
      is_active: !(rate.statusDisplay == 'Active'),
      status: rate.statusDisplay == 'Active' ? 4 : 1,
    };

    toggleRateMutation.mutate(payload, {
      onSuccess: () => {
        logEvent('Tariff Status Changed', {
          id: rate.id,
          previous_status: rate.isActive,
          new_status: !rate.isActive,
        })
      }
    });
  };

  const handleToggleClick = (row: TariffListItem) => {
    setRowToToggle(row);
    setIsToggleModalOpen(true);
  };

  const handleConfirmToggle = () => {
    if (rowToToggle) {
      handleStatusToggle(rowToToggle);
    }
    setIsToggleModalOpen(false);
  };

  const onEdit = (row: TariffListItem) => {
    setSelectedRate(row);
    setIsEditModalOpen(true);
  };

  const onView = (row: TariffListItem) => {
    console.log("Edit row:", row);
    navigate(`/plan-tarrif/details/${row.id}`, {
      state: { initialData: row },
    });
  };

  const onClone = async (row: TariffListItem) => {
    await cloneRate(row.id);
  };


  const columns: TableColumn<TariffListItem>[] = [
    {
      key: "rateName",
      header: "Tariff Name",
      width: "w-48",
      render: (_, row) => (
        <span className="text-sm font-medium text-gray-900">
          {row.rateName}
        </span>
      ),
    },
    {
      key: "utilityService",
      header: "Utility Type",
      width: "w-40",
      render: (value) => <span className="text-sm text-gray-700">{value}</span>,
    },
    {
      key: "rateTypeDisplay",
      header: "Tarrif Type",
      width: "w-40",
      render: (value) => <span className="text-sm text-gray-700">{value}</span>,
    },
    {
      key: "currentVersionNumber",
      header: "Version",
      width: "w-40",
      render: (value) => <span className="text-sm text-gray-700">{value}</span>,
    },
    {
      key: "createdDate",
      header: "Created On",
      render: (value, row) => (
        <div>
          <p className="font-medium">{value ?? "NA"}</p>
          <p className="text-xs text-muted-foreground">
            {row.createdUserRemoteName ?? "NA"}
          </p>
        </div>
      ),
    },
    {
      key: "effectiveDate",
      header: "Effective From",
    },
    {
      key: "expiryDate",
      header: "Effective To",
      render: (value) => <span>{value ?? 'Ongoing'}</span>,

    },

    {
      key: "statusDisplay",
      header: "Status",
      type: "badge",
      render: (value: string) => {
        const className = value == "Active"
          ? "bg-green-100 text-green-800 border-green-200 hover:bg-green-200"
          : "bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200";

        return (
          <span
            className={`text-xs font-medium px-2.5 py-0.5 rounded border ${className}`}
          >
            {value}
          </span>
        );
      },
    },

  ];

  // Handle pagination
  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set("page", newPage.toString());
    setSearchParams(params);
  };

  // Handle search - CRITICAL FIX!
  const handleSearch = (searchTerm: string, paramName: string) => {
    console.log(`Search term: "${searchTerm}", Param: "${paramName}"`);

    const params = new URLSearchParams(searchParams);

    if (searchTerm.trim()) {
      // Set the search param in URL (GenericTable uses 'query' by default)
      params.set(paramName, searchTerm);
    } else {
      params.delete(paramName);
    }

    // Reset to page 1 when searching
    // params.set('page', '1');
    setSearchParams(params);
  };

  const actions: TableAction<TariffListItem>[] = [
    {
      label: "View Tariff",
      onClick: (row) => onView(row),
    },
    {
      label: "Edit Tariff",
      onClick: (row) => onEdit(row),
    },
    {
      label: "Clone Tariff",
      onClick: (row) => onClone(row),
    },
    {
      label: "Make Active",
      hidden: (row) => row.statusDisplay === 'Active' || row.statusDisplay === 'Scheduled',
      onClick: (row) => handleToggleClick(row),
    },
    {
      label: "Make Inactive",
      hidden: (row) => row.statusDisplay === 'Inactive' || row.statusDisplay === 'Scheduled',
      onClick: (row) => handleToggleClick(row),
    },
  ];

  const currentPage = parseInt(searchParams.get("page") || "1");

  return (
    <div>
      <div className="space-y-4">
        {/* Generic Table */}
        <GenericTable
          data={data?.result || []}
          columns={columns}
          searchPlaceholder="Search users..."
          searchParamName="query" // This will be mapped to search_data in API
          searchDebounceMs={300}
          onSearch={handleSearch}
          rowKey="id"
          actions={actions}
          actionsType="dropdown"
          pagination={{
            currentPage: currentPage,
            totalPages: Math.ceil((data?.count || 0) / itemsPerPage),
            totalItems: data?.count || 0,
            itemsPerPage,
            startItem: (currentPage - 1) * itemsPerPage + 1,
            endItem: Math.min(currentPage * itemsPerPage, data?.count || 0),
          }}
          onPageChange={handlePageChange}
          filterPanel={
            <FilterPanel
              currentFilters={filters}
              onApplyFilters={handleApplyFilters}
              onResetFilters={handleResetFilters}
            />
          }
          emptyMessage="No users found"
        />
      </div>

      <EditRateModal
        isOpen={isEditModalOpen}
        onOpenChange={setIsEditModalOpen}
        id={selectedRate?.id!}
        currentVersion={selectedRate?.currentVersionNumber}
      />

      <DeleteConfirmationDialog
        isOpen={isToggleModalOpen}
        onClose={() => setIsToggleModalOpen(false)}
        onConfirm={handleConfirmToggle}
        title={rowToToggle?.isActive ? "Make Inactive" : "Make Active"}
        description={`Are you sure you want to make this tariff ${rowToToggle?.isActive ? 'inactive' : 'active'}?`}
        confirmText="Confirm"
        cancelText="Cancel"
        isLoading={toggleRateMutation.isPending}
      />
    </div>
  );
};

export default TarrifList;
